import { useState, useEffect } from 'react';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface Organization {
  id: string;
  name: string;
  installationId: number;
  subscriptionTier: string;
  role: string;
}

export function DeviceAuthPage() {
  const [userCode, setUserCode] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [status, setStatus] = useState<
    'loading' | 'input' | 'verify' | 'select' | 'success' | 'error'
  >('loading');
  const [error, setError] = useState('');
  const [codeFromUrl, setCodeFromUrl] = useState('');

  useEffect(() => {
    // Track pageview for device auth page
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_location: `${window.location.origin}/auth/device`,
        page_title: 'device-auth',
      });
    }

    // Check if user code is in URL
    const params = new URLSearchParams(window.location.search);
    const code = params.get('user_code');

    if (code) {
      setCodeFromUrl(code);
      setUserCode(code);
      verifyCode(code);
    } else {
      setStatus('input');
    }

    // Fetch user's organizations
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await fetch('/api/organizations');
      if (response.ok) {
        const orgs = await response.json();
        setOrganizations(orgs);
        if (orgs.length === 1) {
          setSelectedOrg(orgs[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  };

  const verifyCode = async (code: string) => {
    setStatus('verify');
    setError('');

    try {
      const response = await fetch(`/api/v1/auth/device/verify/${code}`);
      if (response.ok) {
        const data = await response.json();
        if (data.valid && data.status === 'pending') {
          setStatus('select');
        } else if (data.status === 'authorized') {
          setStatus('success');
        } else {
          setError('This code has already been used or denied');
          setStatus('error');
        }
      } else {
        setError('Invalid or expired code');
        setStatus('error');
      }
    } catch (err) {
      setError('Failed to verify code');
      setStatus('error');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userCode) {
      verifyCode(userCode);
    }
  };

  const handleAuthorize = async () => {
    if (!selectedOrg) {
      setError('Please select an organization');
      return;
    }

    setStatus('loading');
    setError('');

    try {
      const response = await fetch('/api/v1/auth/device/authorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_code: userCode,
          organization_id: selectedOrg,
        }),
      });

      if (response.ok) {
        setStatus('success');
      } else {
        const data = await response.json();
        setError(data.error || 'Authorization failed');
        setStatus('error');
      }
    } catch (err) {
      setError('Failed to authorize device');
      setStatus('error');
    }
  };

  const handleDeny = async () => {
    try {
      await fetch('/api/v1/auth/device/deny', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_code: userCode }),
      });
    } catch (err) {
      console.error('Failed to deny:', err);
    }
    setStatus('error');
    setError('Authorization denied');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #522D6D 0%, #6B3F8C 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          maxWidth: '500px',
          width: '100%',
          background: 'white',
          borderRadius: '12px',
          padding: '48px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        }}
      >
        {status === 'loading' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <h1 style={{ color: '#522D6D', marginBottom: '8px' }}>Loading...</h1>
          </div>
        )}

        {status === 'input' && (
          <>
            <h1 style={{ color: '#522D6D', marginBottom: '16px', textAlign: 'center' }}>
              Device Authorization
            </h1>
            <p style={{ color: '#6b7280', marginBottom: '24px', textAlign: 'center' }}>
              Enter the code displayed on your device
            </p>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                value={userCode}
                onChange={e => setUserCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX"
                maxLength={9}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '24px',
                  textAlign: 'center',
                  letterSpacing: '4px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontFamily: 'monospace',
                }}
              />
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #522D6D 0%, #6B3F8C 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Continue
              </button>
            </form>
          </>
        )}

        {status === 'verify' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <h1 style={{ color: '#522D6D', marginBottom: '8px' }}>Verifying Code</h1>
            <p style={{ color: '#6b7280' }}>Please wait...</p>
          </div>
        )}

        {status === 'select' && (
          <>
            <h1 style={{ color: '#522D6D', marginBottom: '16px', textAlign: 'center' }}>
              Authorize CLI Access
            </h1>
            <div
              style={{
                background: '#f3f4f6',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '24px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                Device Code
              </div>
              <div
                style={{
                  fontSize: '24px',
                  fontFamily: 'monospace',
                  letterSpacing: '4px',
                  color: '#522D6D',
                  fontWeight: '600',
                }}
              >
                {userCode}
              </div>
            </div>

            {organizations.length > 0 && (
              <>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#374151',
                    fontWeight: '500',
                  }}
                >
                  Select Organization
                </label>
                <select
                  value={selectedOrg}
                  onChange={e => setSelectedOrg(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    fontSize: '16px',
                  }}
                >
                  <option value="">Choose an organization...</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.subscriptionTier})
                    </option>
                  ))}
                </select>
              </>
            )}

            {error && (
              <div
                style={{
                  background: '#fee',
                  color: '#c00',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '14px',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleDeny}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Deny
              </button>
              <button
                onClick={handleAuthorize}
                disabled={!selectedOrg}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: selectedOrg
                    ? 'linear-gradient(135deg, #522D6D 0%, #6B3F8C 100%)'
                    : '#e5e7eb',
                  color: selectedOrg ? 'white' : '#9ca3af',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: selectedOrg ? 'pointer' : 'not-allowed',
                }}
              >
                Authorize
              </button>
            </div>
          </>
        )}

        {status === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '48px',
              }}
            >
              ✓
            </div>
            <h1 style={{ color: '#522D6D', marginBottom: '16px' }}>Device Authorized!</h1>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              You can now return to your terminal and continue using the CLI.
            </p>
            <p style={{ fontSize: '14px', color: '#9ca3af' }}>You can close this window.</p>
          </div>
        )}

        {status === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #f5222d 0%, #ff4d4f 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '48px',
              }}
            >
              ✗
            </div>
            <h1 style={{ color: '#522D6D', marginBottom: '16px' }}>Authorization Failed</h1>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>{error}</p>
            <button
              onClick={() => {
                setStatus('input');
                setUserCode('');
                setError('');
              }}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #522D6D 0%, #6B3F8C 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
